import type { DefBounds, Rect, Size } from '@/common/utils/formOverlayLayout'
import {
  calcInitialBounds,
  calcOverlayLocation,
  calcSwingOverlayHostRect,
  updateMagnetic,
} from '@/common/utils/formOverlayLayout'
import { useFloatingOverlayDrag } from '@/common/composables/useFloatingOverlayDrag'
import { computed, onUnmounted, ref, shallowRef, watch, type Ref } from 'vue'

export interface UseFormOverlayPageOptions {
  /** Form anchor (Swing form window pane origin). */
  anchorEl: Ref<HTMLElement | null>
  /** Layer root (Swing JLayeredPane / platform shell). */
  layerEl: Ref<HTMLElement | null>
  overlayEl: Ref<HTMLElement | null>
  defBounds: DefBounds
  initialVisible?: boolean
  onVisibleChange?: (visible: boolean) => void
}

export function resolveOverlayLayerEl(
  anchorEl: HTMLElement | null,
  injectedLayer: HTMLElement | null,
): HTMLElement | null {
  if (injectedLayer) return injectedLayer
  if (!anchorEl) return null
  return anchorEl.closest('.platform-container, .layout-main') as HTMLElement | null
}

export function useFormOverlayPage(options: UseFormOverlayPageOptions) {
  const visible = ref(options.initialVisible ?? false)
  const x = ref(0)
  const y = ref(0)
  const width = ref(0)
  const height = ref(0)
  const magnetic = ref(0)
  const initialized = ref(false)
  const layoutEpoch = ref(0)

  const preferredSize = shallowRef<Size>({ width: 320, height: 240 })

  const effectiveLayerEl = computed(() =>
    resolveOverlayLayerEl(options.anchorEl.value, options.layerEl.value),
  )

  const getHostRect = (): Rect => {
    layoutEpoch.value
    return calcSwingOverlayHostRect(options.anchorEl.value, effectiveLayerEl.value)
  }

  const overlayStyle = computed(() => ({
    left: `${x.value}px`,
    top: `${y.value}px`,
    width: width.value > 0 ? `${width.value}px` : undefined,
    height: height.value > 0 ? `${height.value}px` : undefined,
  }))

  const bumpLayout = () => {
    layoutEpoch.value++
  }

  const { detach: detachDrag } = useFloatingOverlayDrag({
    enabled: visible,
    overlayEl: options.overlayEl,
    getHostRect,
    x,
    y,
    width,
    height,
    magnetic,
  })

  const measurePreferredSize = (contentOnly = true) => {
    const el = options.overlayEl.value
    if (!el) return
    const header = el.querySelector(
      '[data-overlay-drag-handle], .form-overlay-page__handle, .m-expansion .p-accordionheader',
    ) as HTMLElement | null
    const content = el.querySelector('.form-overlay-page__content') as HTMLElement | null
    const headerHeight = header?.offsetHeight ?? 0
    let measuredWidth = Math.max(el.offsetWidth, 200)
    let measuredHeight = headerHeight || 120

    if (contentOnly && content) {
      const contentVisible = content.offsetParent !== null && content.clientHeight > 0
      if (contentVisible) {
        measuredWidth = Math.max(content.scrollWidth, content.offsetWidth, el.offsetWidth, 200)
        measuredHeight = Math.max(
          headerHeight + content.scrollHeight,
          headerHeight + content.offsetHeight,
          120,
        )
      } else {
        measuredWidth = Math.max(header?.scrollWidth ?? 0, header?.offsetWidth ?? 0, 200)
        measuredHeight = Math.max(headerHeight, 27)
      }
    } else {
      const target = content ?? el
      measuredWidth = Math.max(target.scrollWidth, target.offsetWidth, 200)
      measuredHeight = Math.max(target.scrollHeight, target.offsetHeight, 120)
    }

    preferredSize.value = { width: measuredWidth, height: measuredHeight }
  }

  const remeasureBounds = () => {
    if (!visible.value || !initialized.value) return
    measurePreferredSize(true)
    if (options.defBounds.height <= 0) {
      height.value = preferredSize.value.height
    }
    if (options.defBounds.width <= 0) {
      width.value = preferredSize.value.width
    }
    repositionOnHostResize()
  }

  const applyInitialBounds = () => {
    bumpLayout()
    measurePreferredSize()
    const host = getHostRect()
    const bounds = calcInitialBounds(host, options.defBounds, preferredSize.value)
    x.value = bounds.x
    y.value = bounds.y
    width.value = bounds.width
    height.value = bounds.height
    magnetic.value = updateMagnetic(host, bounds)
    initialized.value = true
  }

  const repositionOnHostResize = () => {
    if (!initialized.value || !visible.value) return
    bumpLayout()
    const host = getHostRect()
    const loc = calcOverlayLocation(
      host,
      { x: x.value, y: y.value },
      { width: width.value, height: height.value },
      magnetic.value,
    )
    x.value = loc.x
    y.value = loc.y
    magnetic.value = updateMagnetic(host, {
      x: x.value,
      y: y.value,
      width: width.value,
      height: height.value,
    })
  }

  let resizeObserver: ResizeObserver | null = null

  const attachLayoutObservers = () => {
    detachLayoutObservers()
    resizeObserver = new ResizeObserver(() => {
      repositionOnHostResize()
    })
    const anchor = options.anchorEl.value
    const layer = effectiveLayerEl.value
    if (anchor) resizeObserver.observe(anchor)
    if (layer && layer !== anchor) resizeObserver.observe(layer)
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
  }

  const detachLayoutObservers = () => {
    resizeObserver?.disconnect()
    resizeObserver = null
    window.removeEventListener('scroll', onScrollOrResize, true)
    window.removeEventListener('resize', onScrollOrResize)
  }

  const onScrollOrResize = () => {
    repositionOnHostResize()
  }

  watch(
    [() => options.anchorEl.value, effectiveLayerEl],
    () => {
      detachLayoutObservers()
      attachLayoutObservers()
      if (initialized.value && visible.value) {
        repositionOnHostResize()
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    detachLayoutObservers()
    detachDrag()
  })

  const setVisible = (isVisible: boolean) => {
    visible.value = isVisible
    options.onVisibleChange?.(isVisible)
    if (isVisible) {
      requestAnimationFrame(() => {
        if (!initialized.value) {
          applyInitialBounds()
        } else {
          repositionOnHostResize()
        }
      })
    }
  }

  const isVisible = () => visible.value

  return {
    visible,
    overlayStyle,
    setVisible,
    isVisible,
    applyInitialBounds,
    remeasureBounds,
    effectiveLayerEl,
  }
}
